import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';
import BulkActions from '../components/BulkActions';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  CheckSquare, 
  Sun, 
  Moon, 
  Sparkles,
  Clock,
  User,
  ChevronRight,
  Filter,
  ExternalLink
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  // Get initial filter from URL params or default to 'all'
  // Map URL param values to internal filter values
  const getFilterFromParams = () => {
    const statusParam = searchParams.get('status');
    if (!statusParam) return 'all';
    
    // Map status values - handle both URL encoded and plain values
    const statusMap = {
      'in progress': 'in-progress',
      'in-progress': 'in-progress',
      'new': 'new',
      'completed': 'completed',
      'my-tasks': 'my-tasks',
      'all': 'all'
    };
    return statusMap[statusParam.toLowerCase()] || 'all';
  };
  
  const [filter, setFilter] = useState(getFilterFromParams());

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [fetchTasks, fetchUsers]);

  // Task selection handlers
  const handleTaskSelect = (taskId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        const newSelection = prev.filter(id => id !== taskId);
        if (newSelection.length === 0) setSelectionMode(false);
        return newSelection;
      } else {
        if (!selectionMode) setSelectionMode(true);
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = () => {
    const taskIds = filteredTasks.map(t => t.id);
    const allSelected = taskIds.every(id => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks(prev => prev.filter(id => !taskIds.includes(id)));
      if (selectedTasks.length === taskIds.length) setSelectionMode(false);
    } else {
      setSelectedTasks(prev => [...new Set([...prev, ...taskIds])]);
      setSelectionMode(true);
    }
  };

  const clearSelection = () => {
    setSelectedTasks([]);
    setSelectionMode(false);
  };

  const handleBulkActionComplete = () => {
    fetchTasks();
    clearSelection();
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
      acknowledged: 'status-acknowledged',
      'in progress': 'status-in-progress',
      completed: 'status-completed'
    };
    return styles[status] || '';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return styles[priority] || '';
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'my-tasks') return task.assigned_to === user?.id;
    if (filter === 'assigned-by-me') return task.assigned_by === user?.id;
    if (filter === 'new') return task.status === 'new';
    if (filter === 'in-progress') return task.status === 'in progress';
    if (filter === 'completed') return task.status === 'completed';
    return true;
  });

  const myTasks = tasks.filter(t => t.assigned_to === user?.id);
  const assignedByMe = tasks.filter(t => t.assigned_by === user?.id);
  const newTasks = tasks.filter(t => t.status === 'new');
  const inProgressTasks = tasks.filter(t => t.status === 'in progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading history...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="px-4 sm:px-6 py-3 sm:py-4 pl-14 lg:pl-6">
            <h1 className="font-heading text-xl sm:text-2xl font-bold">Task History</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">{tasks.length} total tasks</p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className={`cursor-pointer hover-lift ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{tasks.length}</p>
              <p className="text-sm text-muted-foreground">All Tasks</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover-lift ${filter === 'my-tasks' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('my-tasks')}
            data-testid="filter-my-tasks"
          >
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{myTasks.length}</p>
              <p className="text-sm text-muted-foreground">My Tasks</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover-lift ${filter === 'new' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('new')}
            data-testid="filter-new"
          >
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-purple-500">{newTasks.length}</p>
              <p className="text-sm text-muted-foreground">New</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover-lift ${filter === 'in-progress' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('in-progress')}
            data-testid="filter-in-progress"
          >
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-amber-500">{inProgressTasks.length}</p>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover-lift ${filter === 'completed' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('completed')}
            data-testid="filter-completed"
          >
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-emerald-500">{completedTasks.length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Task List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {filter === 'all' && 'All Tasks'}
              {filter === 'my-tasks' && 'My Tasks'}
              {filter === 'assigned-by-me' && 'Tasks I Assigned'}
              {filter === 'new' && 'New Tasks'}
              {filter === 'in-progress' && 'In Progress Tasks'}
              {filter === 'completed' && 'Completed Tasks'}
            </CardTitle>
            <div className="flex items-center gap-2">
              {filteredTasks.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-xs"
                  data-testid="select-all-history"
                >
                  {filteredTasks.every(t => selectedTasks.includes(t.id)) 
                    ? 'Deselect All' 
                    : 'Select All'}
                </Button>
              )}
              <Badge variant="outline">{filteredTasks.length} tasks</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-24rem)]">
              {filteredTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <CheckSquare className="w-12 h-12 mb-4 opacity-20" />
                  <p>No tasks found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTasks.map((task) => {
                    const isSelected = selectedTasks.includes(task.id);
                    return (
                      <div
                        key={task.id}
                        className={`task-card p-4 rounded-xl border bg-card cursor-pointer transition-all ${
                          task.ai_priority === 'high' && task.status !== 'completed' ? 'ai-glow' : ''
                        } ${isSelected ? 'ring-2 ring-primary' : 'hover:bg-accent/50'}`}
                        onClick={() => !selectionMode && navigate(`/tasks/${task.id}`)}
                        data-testid={`history-task-${task.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleTaskSelect(task.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`history-checkbox-${task.id}`}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-medium">{task.name}</h3>
                              {task.ai_priority && (
                                <Badge variant="outline" className={`${getPriorityBadge(task.ai_priority)} text-xs`}>
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  {task.ai_priority}
                                </Badge>
                              )}
                              {task.external_submitter && (
                                <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Intake
                                </Badge>
                              )}
                            </div>
                            {task.external_submitter && (
                              <p className="text-xs text-blue-400 mb-1">
                                Submitted by: {task.external_submitter.email}
                              </p>
                            )}
                            {task.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Due: {format(parseISO(task.due_date), 'PP')}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assigned_to === user?.id ? (
                                  <>From: {task.assigned_by_name}</>
                                ) : (
                                  <>To: {task.assigned_to_name}</>
                                )}
                              </span>
                              <span>
                                Created: {formatDistanceToNow(parseISO(task.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge variant="outline" className={getStatusBadge(task.status)}>
                              {task.status}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </main>

      {/* Bulk Actions Bar */}
      <BulkActions
        selectedTasks={selectedTasks}
        onClearSelection={clearSelection}
        onActionComplete={handleBulkActionComplete}
        token={token}
        users={allUsers}
      />
      </div>
    </Sidebar>
  );
};

export default TaskHistory;
